import { trackError } from '@billy/track-error'
import { useRef } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../../../config/queryClient'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { FEC_IMPORT_LIST_MAX_LIMIT } from '../constants/fecImportList'
import { FEC_IMPORT_POLLING_INTERVAL_MS } from '../constants/fecImportPolling'
import { FecImportItem, fetchFecImportList } from '../query-api'
import { getFecImportFailedPollingIntervalMs } from '../utils/getFecImportFailedPollingIntervalMs'
import { isFecImportInProgress } from '../utils/isFecImportInProgress'

interface UseFetchFecImportsOptions {
  onError?: (error: unknown) => void
  onSuccess?: () => void
  refetchOnMount?: boolean | 'always'
  shouldPoll?: (fecImports: FecImportItem[]) => boolean
  /** Poll with growing pauses (see `getFecImportFailedPollingIntervalMs`) while this holds and nothing else needs a poll. */
  shouldPollWithBackoff?: (fecImports: FecImportItem[]) => boolean
}

interface UseFetchFecImportsResult {
  error: unknown
  fecImports: FecImportItem[]
  isError: boolean
  isFetchedAfterMount: boolean
  isLoading: boolean
  isRefetchError: boolean
  refetch: () => Promise<unknown>
}

export const useFetchFecImports = (options?: UseFetchFecImportsOptions): UseFetchFecImportsResult => {
  const { organization } = useUserOrganization()
  // The list is polled: say once that it is incomplete, not on every poll.
  const reportedIncompleteTotalRef = useRef<number>()
  // When the current stretch of backoff polling began. Setting it again to the same value is harmless, as the
  // interval is worked out again on every render.
  const backoffStartedAtRef = useRef<number | null>(null)

  const {
    data: fecImportsData,
    error,
    isError,
    isFetchedAfterMount,
    isIdle,
    isLoading,
    refetch,
  } = useQuery(
    [QueryKeys.FecImports, organization?.id],
    () =>
      fetchFecImportList(organization?.id || '', {
        limit: FEC_IMPORT_LIST_MAX_LIMIT,
        open: true,
      }).then(({ items, total }) => {
        if (total > items.length && reportedIncompleteTotalRef.current !== total) {
          reportedIncompleteTotalRef.current = total
          trackError(new Error(`FEC imports list is incomplete: ${items.length} of ${total}`))
        }

        return items
      }),
    {
      enabled: !!organization?.id,
      onError: options?.onError,
      onSuccess: options?.onSuccess,
      refetchOnMount: options?.refetchOnMount,
      refetchInterval: (fecImports) => {
        const imports = fecImports || []

        if (options?.shouldPoll?.(imports) || imports.some((fecImport) => isFecImportInProgress(fecImport))) {
          backoffStartedAtRef.current = null

          return FEC_IMPORT_POLLING_INTERVAL_MS
        }

        if (options?.shouldPollWithBackoff?.(imports)) {
          backoffStartedAtRef.current ??= Date.now()

          return getFecImportFailedPollingIntervalMs(Date.now() - backoffStartedAtRef.current)
        }

        backoffStartedAtRef.current = null

        return false
      },
      staleTime: defaultStaleTime,
    },
  )

  return {
    error,
    fecImports: fecImportsData || [],
    isError: isError && fecImportsData === undefined,
    isFetchedAfterMount,
    isLoading: isLoading || isIdle,
    isRefetchError: isError && fecImportsData !== undefined,
    refetch,
  }
}
